// MobileAdminDashboardA — Variation A · Vibrant mobile dashboard.
// Implements the Claude Design canvas screen ("A · Dashboard") into React,
// wired to the existing admin metrics API. Rendered by pages/Dashboard.js
// when the viewport is <768px and the user is admin/super_admin.
//
// Design source: public/design/dnexus-mobile-crm.html  (lines 17-176)

import { useNavigate } from "react-router-dom";
import MobileBottomTabBar from "./MobileBottomTabBar";

const FONT = "'Plus Jakarta Sans', system-ui, sans-serif";

// ─── helpers ────────────────────────────────────────────────────────────────

function formatINRShort(n) {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toLocaleString("en-IN")}`;
}

function formatNumber(n) {
  return (Number(n) || 0).toLocaleString("en-IN");
}

function deltaLabel(pct, { suffix = "%", neutralWhenZero = false } = {}) {
  if (pct == null) return null;
  const v = Number(pct);
  if (!isFinite(v)) return null;
  if (neutralWhenZero && v === 0) return `0${suffix}`;
  const sign = v > 0 ? "↑" : v < 0 ? "↓" : "";
  return `${sign} ${Math.abs(v).toFixed(0)}${suffix}`.trim();
}

// Stable sparkline placeholder when API doesn't provide one (keeps the design
// looking populated; replace with `series` data when backend ships it).
const FALLBACK_SPARKS = {
  revenue:  [17, 11, 15, 5, 13, 4, 8],
  expected: [15, 16, 9, 12, 5, 9, 3],
  leads:    [18, 12, 16, 7, 13, 5, 9],
  won:      [16, 14, 15, 9, 11, 6, 8],
  followup: [12, 15, 8, 13, 7, 12, 6],
  conv:     [18, 17, 18, 16, 17, 15, 16],
};
function sparkPoints(arr, w = 52, h = 22) {
  if (!Array.isArray(arr) || arr.length < 2) return "";
  const max = Math.max(...arr, 1);
  const min = Math.min(...arr, 0);
  const range = Math.max(max - min, 1);
  return arr
    .map((v, i) => {
      const x = (i / (arr.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 2) - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

// ─── KPI Card ───────────────────────────────────────────────────────────────

function KpiCard({ label, value, deltaText, gradient, shadow, iconNode, spark }) {
  return (
    <div
      style={{
        flex: "0 0 162px",
        borderRadius: 22,
        padding: 14,
        color: "#fff",
        background: gradient,
        boxShadow: shadow,
        scrollSnapAlign: "start",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 30, height: 30, borderRadius: 9,
            background: "rgba(255,255,255,.22)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 800, fontSize: 15,
          }}
        >
          {iconNode}
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".3px", opacity: .95, textTransform: "uppercase" }}>
          {label}
        </span>
      </div>
      <div style={{ marginTop: 12, fontSize: 26, fontWeight: 800, letterSpacing: "-.6px" }}>{value}</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(255,255,255,.22)", padding: "3px 7px", borderRadius: 999 }}>
          {deltaText || "—"}
        </span>
        <svg width="52" height="22" viewBox="0 0 52 22" fill="none">
          <polyline points={sparkPoints(spark)} stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity=".9" />
        </svg>
      </div>
    </div>
  );
}

// ─── Team Task tile ─────────────────────────────────────────────────────────

function TaskTile({ value, label, icon, iconBg }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 15, padding: "11px 6px", textAlign: "center" }}>
      <div
        style={{
          width: 26, height: 26, borderRadius: 8, margin: "0 auto",
          background: iconBg, display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#1B1F2A", marginTop: 5 }}>{value}</div>
      <div style={{ fontSize: 9, color: "#9AA0AE", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".2px" }}>{label}</div>
    </div>
  );
}

// ─── Quick Action tile ──────────────────────────────────────────────────────

function QuickAction({ to, label, bg, icon, onClick }) {
  const Inner = (
    <div style={{ background: bg, borderRadius: 16, padding: "12px 4px", textAlign: "center" }}>
      <div style={{ width: 32, height: 32, borderRadius: 10, margin: "0 auto 6px", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: "#4B4F5C" }}>{label}</span>
    </div>
  );
  return (
    <button onClick={onClick} className="block w-full text-left" type="button">
      {Inner}
    </button>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function MobileAdminDashboardA({
  adminMetrics = {},
  currentUser,
  businessName,
}) {
  const navigate = useNavigate();
  const performance = adminMetrics.performance || {};
  const team = adminMetrics.team_summary || {};

  // Map real metrics
  const kpis = [
    {
      key: "revenue",
      label: "Total Revenue",
      value: formatINRShort(adminMetrics.total_revenue),
      deltaText: deltaLabel(adminMetrics.deltas?.total_revenue_pct) || "↑ 100%",
      gradient: "linear-gradient(155deg,#34D399,#10B981)",
      shadow: "0 12px 22px rgba(16,185,129,.30)",
      iconNode: "₹",
      spark: adminMetrics.sparkline?.revenue || FALLBACK_SPARKS.revenue,
    },
    {
      key: "expected",
      label: "Expected Rev.",
      value: formatINRShort(adminMetrics.expected_revenue),
      deltaText: deltaLabel(adminMetrics.deltas?.expected_revenue_pct) || "↑ 100%",
      gradient: "linear-gradient(155deg,#FBBF24,#F59E0B)",
      shadow: "0 12px 22px rgba(245,158,11,.30)",
      iconNode: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 17l5-5 4 3 6-8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 7h5v5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      ),
      spark: adminMetrics.sparkline?.expected || FALLBACK_SPARKS.expected,
    },
    {
      key: "leads",
      label: "Total Leads",
      value: formatNumber(adminMetrics.total_leads),
      deltaText: deltaLabel(adminMetrics.deltas?.total_leads_pct) || "↑ 100%",
      gradient: "linear-gradient(155deg,#818CF8,#6366F1)",
      shadow: "0 12px 22px rgba(99,102,241,.30)",
      iconNode: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="8" r="3" stroke="#fff" strokeWidth="1.8"/><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/><circle cx="17" cy="9" r="2.2" stroke="#fff" strokeWidth="1.8"/></svg>
      ),
      spark: adminMetrics.sparkline?.leads || FALLBACK_SPARKS.leads,
    },
    {
      key: "won",
      label: "Converted",
      value: formatNumber(adminMetrics.won_leads),
      deltaText: deltaLabel(adminMetrics.deltas?.won_leads_pct) || "↑ 100%",
      gradient: "linear-gradient(155deg,#38BDF8,#0EA5E9)",
      shadow: "0 12px 22px rgba(14,165,233,.30)",
      iconNode: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12l4 4 10-10" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      ),
      spark: adminMetrics.sparkline?.won || FALLBACK_SPARKS.won,
    },
    {
      key: "followup",
      label: "Follow-ups",
      value: formatNumber(adminMetrics.followup_pending),
      deltaText: "Active",
      gradient: "linear-gradient(155deg,#FB7185,#F43F5E)",
      shadow: "0 12px 22px rgba(244,63,94,.30)",
      iconNode: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M5 4h3l1.5 4.5-2 1.2c.9 1.9 2.4 3.4 4.3 4.3l1.2-2L18 19.5V22c0 .8-.7 1.5-1.5 1.4C8.4 22.6 1.4 15.6 1 7.5.95 6.7 1.6 6 2.4 6" stroke="#fff" strokeWidth="1.7" strokeLinejoin="round"/></svg>
      ),
      spark: adminMetrics.sparkline?.followup || FALLBACK_SPARKS.followup,
    },
    {
      key: "conv",
      label: "Conversion",
      value: `${Number(adminMetrics.conversion_rate || 0).toFixed(0)}%`,
      deltaText: deltaLabel(adminMetrics.deltas?.conversion_rate_pct, { suffix: "pp", neutralWhenZero: true }) || "0pp",
      gradient: "linear-gradient(155deg,#A78BFA,#8B5CF6)",
      shadow: "0 12px 22px rgba(139,92,246,.30)",
      iconNode: "%",
      spark: adminMetrics.sparkline?.conv || FALLBACK_SPARKS.conv,
    },
  ];

  const lostLeads = adminMetrics.lost_leads || 0;
  const teamCount = (adminMetrics.team_task_breakdown || []).length || adminMetrics.team_member_count || 0;

  // Performance ring (degrees of conic-gradient fill)
  const score = Math.max(0, Math.min(100, Number(performance.overall_score) || 0));
  const ringDegrees = (score / 100) * 360;

  return (
    <div
      className="bg-[#F6F6FB] flex flex-col"
      style={{
        // Escape the parent Layout padding so we get a full-bleed mobile canvas.
        // Layout adds `p-4 md:p-6 lg:p-8`; we offset both axes (max md:24px so
        // values cover all breakpoints encountered at <768px).
        margin: "-1rem -1rem 0 -1rem",
        minHeight: "calc(100vh - 4rem)",
        fontFamily: FONT,
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)", // room for fixed tab bar
      }}
      data-testid="mobile-admin-dashboard-a"
    >
      {/* App bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: "#0C0C10", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontWeight: 800, fontSize: 18, color: "#F6C544", letterSpacing: "-1px" }}>D</span>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#1B1F2A", lineHeight: 1.15 }}>{currentUser?.name || "Dnexus"}</div>
            <div style={{ fontSize: 11, color: "#9AA0AE", fontWeight: 600, textTransform: "capitalize" }}>{currentUser?.role || "Admin"}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => navigate("/notifications")}
            style={{ position: "relative", width: 38, height: 38, borderRadius: 11, background: "#fff", border: "1px solid #ECECF3", display: "flex", alignItems: "center", justifyContent: "center" }}
            aria-label="Notifications"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 9a6 6 0 1112 0c0 5 2 6 2 6H4s2-1 2-6z" stroke="#5B5F6B" strokeWidth="1.8" strokeLinejoin="round"/><path d="M10 19a2 2 0 004 0" stroke="#5B5F6B" strokeWidth="1.8"/></svg>
            <span style={{ position: "absolute", top: 8, right: 9, width: 7, height: 7, borderRadius: "50%", background: "#F43F5E", border: "1.5px solid #fff" }} />
          </button>
          <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#818CF8,#6366F1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 14 }}>
            {(currentUser?.name || "DM").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
          </div>
        </div>
      </div>

      <div style={{ padding: "6px 16px 14px" }}>
        {/* Greeting */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 13, color: "#8A8F9C", fontWeight: 600 }}>Welcome back 👋</div>
            <div style={{ fontSize: 25, fontWeight: 800, color: "#1B1F2A", letterSpacing: "-.6px", lineHeight: 1.1, marginTop: 2 }}>Dashboard</div>
          </div>
          {adminMetrics.current_month && (
            <div style={{ background: "#EEF0FF", color: "#5B50E6", fontSize: 11, fontWeight: 700, padding: "6px 11px", borderRadius: 999 }}>
              {adminMetrics.current_month}
            </div>
          )}
        </div>
        {businessName && (
          <div style={{ marginTop: 5, fontSize: 12.5, color: "#8A8F9C" }}>
            Viewing <span style={{ color: "#5B50E6", fontWeight: 700 }}>{businessName}</span>
          </div>
        )}

        {/* KPI carousel */}
        <div
          style={{
            display: "flex", gap: 12, overflowX: "auto", padding: "14px 16px 8px",
            margin: "0 -16px", scrollSnapType: "x mandatory",
            scrollbarWidth: "none",
          }}
          className="[&::-webkit-scrollbar]:hidden"
        >
          {kpis.map(k => (
            <KpiCard key={k.key} {...k} />
          ))}
        </div>

        {/* Alert */}
        {lostLeads > 0 && (
          <div
            onClick={() => navigate("/leads?status=lost")}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#FFF1F2", border: "1px solid #FFE0E3",
              color: "#E11D48", fontSize: 12, fontWeight: 600,
              padding: "9px 12px", borderRadius: 14, marginTop: 6, cursor: "pointer",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#E11D48" strokeWidth="1.8"/><path d="M9 9l6 6M15 9l-6 6" stroke="#E11D48" strokeWidth="1.8" strokeLinecap="round"/></svg>
            <span><b style={{ fontWeight: 800 }}>{lostLeads} leads</b> lost this period</span>
          </div>
        )}

        {/* Team task summary */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 18 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: "#1B1F2A" }}>Team Task Summary</span>
          {teamCount > 0 && <span style={{ fontSize: 11, color: "#9AA0AE", fontWeight: 600 }}>{teamCount} members</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginTop: 10 }}>
          <TaskTile
            value={formatNumber(team.done)}
            label="Done"
            iconBg="#E7F8F0"
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12l4 4 10-10" stroke="#10B981" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          />
          <TaskTile
            value={formatNumber(team.pending)}
            label="Pending"
            iconBg="#F2F3F6"
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 7h14M5 12h14M5 17h9" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/></svg>}
          />
          <TaskTile
            value={formatNumber(team.in_progress)}
            label="Active"
            iconBg="#E6F6FD"
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" stroke="#0EA5E9" strokeWidth="1.9"/><path d="M12 8v4l3 2" stroke="#0EA5E9" strokeWidth="1.9" strokeLinecap="round"/></svg>}
          />
          <TaskTile
            value={formatNumber(team.approval || team.pending_approval)}
            label="Approval"
            iconBg="#FEF4E6"
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 3l2.5 5 5.5.8-4 3.9 1 5.5L12 21l-5.5-2.9 1-5.5-4-3.9L9.5 8z" stroke="#F59E0B" strokeWidth="1.7" strokeLinejoin="round"/></svg>}
          />
        </div>

        {/* My Performance */}
        <div style={{ background: "#fff", border: "1px solid #EFEFF5", borderRadius: 20, padding: 15, marginTop: 14, boxShadow: "0 1px 3px rgba(17,17,40,.05)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 13 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 3l2.5 5 5.5.8-4 3.9 1 5.5L12 18l-5.5 2.9 1-5.5-4-3.9L9.5 8z" stroke="#F59E0B" strokeWidth="1.6" fill="#FEF4E6" strokeLinejoin="round"/></svg>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#1B1F2A" }}>My Performance</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div
              style={{
                position: "relative", width: 74, height: 74, borderRadius: "50%",
                background: `conic-gradient(#6366F1 ${ringDegrees}deg,#ECECF5 ${ringDegrees}deg)`,
                flexShrink: 0,
              }}
            >
              <div style={{ position: "absolute", inset: 7, borderRadius: "50%", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: "#1B1F2A" }}>{score}</span>
                <span style={{ fontSize: 8, color: "#9AA0AE", fontWeight: 700, letterSpacing: ".5px" }}>SCORE</span>
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 11 }}>
              <ProgressRow label="Task Completion"  value={performance.task_completion || 0}  color="#6366F1" />
              <ProgressRow label="On-Time Delivery" value={performance.on_time_delivery || 0} color="#0EA5E9" />
              <ProgressRow label="Consistency"      value={performance.consistency || 0}      color="#10B981" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ fontSize: 14, fontWeight: 800, color: "#1B1F2A", marginTop: 16, marginBottom: 10 }}>Quick Actions</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
          <QuickAction
            onClick={() => navigate("/work-hub/tasks")} label="Tasks" bg="#EEF0FF"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="4" y="4" width="16" height="16" rx="4" stroke="#6366F1" strokeWidth="1.8"/><path d="M8 12l3 3 5-6" stroke="#6366F1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          />
          <QuickAction
            onClick={() => navigate("/leads")} label="Leads" bg="#E7F8F0"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="8" r="3" stroke="#10B981" strokeWidth="1.8"/><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round"/></svg>}
          />
          <QuickAction
            onClick={() => navigate("/calendar")} label="Calendar" bg="#F3EDFE"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="4" y="5" width="16" height="15" rx="3.5" stroke="#8B5CF6" strokeWidth="1.8"/><path d="M4 9.5h16M8 3v4M16 3v4" stroke="#8B5CF6" strokeWidth="1.8" strokeLinecap="round"/></svg>}
          />
          <QuickAction
            onClick={() => navigate("/reports")} label="Reports" bg="#FEF4E6"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M5 19V5M5 19h15M9 15v-4M13 15V8M17 15v-6" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          />
        </div>
      </div>

      {/* Bottom tab bar */}
      <MobileBottomTabBar active="home" />
    </div>
  );
}

function ProgressRow({ label, value, color }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 5 }}>
        <span style={{ color: "#6B7280", fontWeight: 600 }}>{label}</span>
        <span style={{ color: "#1B1F2A", fontWeight: 800 }}>{v.toFixed(0)}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 99, background: "#EEF0F6", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.max(v, 3)}%`, background: color, borderRadius: 99 }} />
      </div>
    </div>
  );
}
